import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Badge,
  BadgeText,
  Box,
  Button,
  ButtonText,
  HStack,
  Heading,
  Icon,
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  Pressable,
  Progress,
  ProgressFilledTrack,
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
  Spinner,
  Text,
  VStack,
  ChevronDownIcon,
} from '@gluestack-ui/themed';
import { Info } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AccessibilityInfo } from 'react-native';
import type { ModuleMetadataWorkingMode } from '@/common/api/models/moduleMetadataWorkingMode';
import type { WorkingModeDiff } from '@/common/api/models/workingModeDiff';
import {
  getGetModuleVersionQueryKey,
  getGetModulesQueryKey,
  useApplyModuleWorkingMode,
  useGetModuleWorkingModeDiff,
} from '@/common/api/endpoints/modules/modules';
import { getGetTasksByModuleVersionQueryKey } from '@/common/api/endpoints/tasks/tasks';
import { getGetAllJobsQueryKey, useGetJobStatus } from '@/common/api/endpoints/jobs/jobs';
import { useUserJobs } from '@/common/hooks/useUserJobs';
import { useDialog } from '@/common/context/DialogContext';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import ContentGroup from '@/common/components/ContentGroup';
import type { Task } from '@/common/api/models/task';
import { useModuleJobStore } from '@/lecturer/ModuleEditor/hooks/useModuleJobStore';
import WorkingModeApplyDialog from './WorkingModeApplyDialog';
import {
  countDiffChanges,
  countGovernedActivities,
  MODE_INFO_RULE_KEYS,
  MODE_LABEL_KEYS,
  resolveBlockVisualState,
  resolveLockedWorkflowState,
  summarizeDeltaLedger,
  WORKING_MODE_OPTIONS,
  type WorkingModeWire,
} from './workingModeDisplay';

interface WorkingModeBlockProps {
  moduleId: string;
  version: string;
  persistedWorkingMode?: ModuleMetadataWorkingMode;
  isDraft: boolean;
  /** Real module workflow state — used for accurate version-locked copy. */
  workflowState?: string;
  formIsDirty: boolean;
  tasks?: Task[];
  expectedEtag?: string | null;
}

const coerceWorkingMode = (mode: ModuleMetadataWorkingMode | undefined): WorkingModeWire => (
  mode ?? 'custom'
);

const WORKING_MODE_APPLY = 'working_mode_apply';

const jobMatchesModuleVersion = (
  job: { type: string; details?: { moduleId?: string; version?: string; [key: string]: unknown } },
  moduleId: string,
  version: string,
): boolean => {
  if (job.type !== WORKING_MODE_APPLY) return false;
  const details = job.details ?? {};
  const jobModuleId = details.moduleId ?? (details.module_id as string | undefined);
  const jobVersion = details.version as string | undefined;
  return jobModuleId === moduleId && jobVersion === version;
};

const WorkingModeBlock: React.FC<WorkingModeBlockProps> = ({
  moduleId,
  version,
  persistedWorkingMode,
  isDraft,
  workflowState,
  formIsDirty,
  tasks,
  expectedEtag,
}) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const queryClient = useQueryClient();
  const [reduceMotion, setReduceMotion] = useState(false);

  const [infoOpen, setInfoOpen] = useState(false);
  const storedApplyJobId = useModuleJobStore(
    (state) => state.workingModeApplyJobs[`${moduleId}:${version}`],
  );
  const setWorkingModeApplyJob = useModuleJobStore((state) => state.setWorkingModeApplyJob);
  const clearWorkingModeApplyJob = useModuleJobStore((state) => state.clearWorkingModeApplyJob);
  const { data: jobInfo } = useUserJobs();

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion).catch(() => undefined);
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion);
    return () => subscription.remove();
  }, []);

  const persistedMode = coerceWorkingMode(persistedWorkingMode);
  const [proposedMode, setProposedMode] = useState<WorkingModeWire>(persistedMode);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState<WorkingModeWire>(persistedMode);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(storedApplyJobId ?? null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const userSelectChangeRef = useRef(false);
  /** Prevents re-attaching a job we already finished handling (stale jobs cache race). */
  const handledTerminalJobRef = useRef<string | null>(null);

  useEffect(() => {
    setProposedMode(persistedMode);
  }, [persistedMode, moduleId, version]);

  const runningApplyJobId = useMemo(() => {
    const match = jobInfo?.runningJobs?.find((job) => jobMatchesModuleVersion(job, moduleId, version));
    return match?.id ?? null;
  }, [jobInfo?.runningJobs, moduleId, version]);

  const recentFailedApplyJobIds = useMemo(() => {
    const ids = new Set<string>();
    for (const job of jobInfo?.recentFailedJobs ?? []) {
      if (jobMatchesModuleVersion(job, moduleId, version)) {
        ids.add(job.id);
      }
    }
    return ids;
  }, [jobInfo?.recentFailedJobs, moduleId, version]);

  // Re-attach to an in-flight apply after navigation / remount.
  // Server jobs list is canonical; persisted store is the fast path.
  useEffect(() => {
    if (activeJobId) return;

    const candidateId = storedApplyJobId ?? runningApplyJobId;
    if (!candidateId) return;
    // Stale cache / persisted store can still point at a finished failure — do not re-attach.
    if (handledTerminalJobRef.current === candidateId) return;
    if (recentFailedApplyJobIds.has(candidateId)) {
      handledTerminalJobRef.current = candidateId;
      clearWorkingModeApplyJob(moduleId, version);
      return;
    }

    setActiveJobId(candidateId);
    if (!storedApplyJobId) {
      setWorkingModeApplyJob(moduleId, version, candidateId);
    }
  }, [
    activeJobId,
    storedApplyJobId,
    runningApplyJobId,
    recentFailedApplyJobIds,
    moduleId,
    version,
    setWorkingModeApplyJob,
    clearWorkingModeApplyJob,
  ]);

  const diffParams = useMemo(
    () => ({ target: proposedMode }),
    [proposedMode],
  );

  const {
    data: diffResponse,
    isLoading: isDiffLoading,
    isError: isDiffError,
    error: diffQueryError,
    refetch: refetchDiff,
  } = useGetModuleWorkingModeDiff(
    moduleId,
    version,
    diffParams,
    {
      query: {
        enabled: isDraft,
        staleTime: 0,
        select: (response) => response?.data?.data,
      },
    },
  );

  const diff = diffResponse as WorkingModeDiff | undefined;

  const applyMutation = useApplyModuleWorkingMode({
    mutation: {
      onSuccess: async (response) => {
        const payload = response?.data?.data;
        setApplyError(null);
        setDialogOpen(false);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetModuleVersionQueryKey(moduleId, version) }),
          queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() }),
          queryClient.invalidateQueries({
            queryKey: getGetTasksByModuleVersionQueryKey(moduleId, version, { include: 'resources' }),
          }),
          queryClient.invalidateQueries({
            queryKey: [`/modules/${moduleId}/versions/${version}/working-mode/diff`],
          }),
        ]);
        if (payload?.jobId) {
          handledTerminalJobRef.current = null;
          setActiveJobId(payload.jobId);
          setWorkingModeApplyJob(moduleId, version, payload.jobId);
          // Refetch the job tray immediately so it appears without waiting for the poll interval.
          queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({}) });
          queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({ completed: true }) });
        } else {
          showToastMessage(
            'success',
            t('lecturer.moduleEditor.workingMode.messages.applySuccess'),
          );
        }
      },
      onError: (error) => {
        const { message } = reportApiError(
          error,
          t('lecturer.moduleEditor.workingMode.messages.applyError'),
        );
        setApplyError(message);
      },
    },
  });

  const {
    data: trackedJob,
    isError: isTrackedJobError,
    isPending: isTrackedJobPending,
  } = useGetJobStatus(activeJobId ?? null, undefined, {
    query: {
      enabled: !!activeJobId,
      retry: false,
      refetchInterval: (query) => {
        if (query.state.error) return false;
        const jobs = query.state.data?.data?.data as { status?: string }[] | undefined;
        const job = jobs?.[0];
        if (!job) return 5000;
        if (job.status === 'running' || job.status === 'pending') return 5000;
        return false;
      },
      select: (response) => {
        const jobs = response.data?.data as { status?: string; currentStage?: string; progress?: number }[] | undefined;
        return jobs?.[0];
      },
    },
  });

  // Applying UI only — completion/cache invalidation lives in useModuleWorkingModeApplyJob
  // (module-editor scoped) so oral/essay caches refresh even after leaving Settings.
  const isJobApplying = !!activeJobId && !isTrackedJobError && (
    isTrackedJobPending
    || trackedJob?.status === 'running'
    || trackedJob?.status === 'pending'
  );

  // Drop local tracking when the editor-level watcher clears the persisted job id.
  useEffect(() => {
    if (!storedApplyJobId && activeJobId) {
      if (handledTerminalJobRef.current !== activeJobId) {
        handledTerminalJobRef.current = activeJobId;
      }
      setActiveJobId(null);
    }
  }, [storedApplyJobId, activeJobId]);

  // Local safety: stop showing Applying if the job 404s / finishes while this block is mounted.
  useEffect(() => {
    if (!activeJobId) return;
    if (isTrackedJobError) {
      handledTerminalJobRef.current = activeJobId;
      setActiveJobId(null);
      return;
    }
    if (!trackedJob) return;
    if (trackedJob.status !== 'success' && trackedJob.status !== 'failed') return;
    handledTerminalJobRef.current = activeJobId;
    setActiveJobId(null);
  }, [activeJobId, trackedJob, isTrackedJobError]);

  const isApplying = applyMutation.isPending || isJobApplying;
  const governedActivityCount = countGovernedActivities(tasks, persistedMode);

  const visualState = resolveBlockVisualState({
    persistedMode,
    isDraft,
    isApplying,
    diff,
    governedActivityCount,
  });

  const lockedWorkflowState = resolveLockedWorkflowState(workflowState);
  const lockedStateLabel = t(
    `lecturer.moduleEditor.moduleEditor.versionSelector.states.${lockedWorkflowState}`,
  );

  const changeCount = countDiffChanges(diff?.activities);
  const ledger = summarizeDeltaLedger(diff?.activities);

  const runApply = useCallback(async (target: WorkingModeWire) => {
    setApplyError(null);
    await applyMutation.mutateAsync({
      moduleId,
      version,
      data: {
        target,
        expectedEtag: expectedEtag ?? undefined,
      },
    });
  }, [applyMutation, expectedEtag, moduleId, version]);

  useEffect(() => {
    if (!userSelectChangeRef.current || isDiffLoading || !diff) return;
    if (!isDraft) {
      userSelectChangeRef.current = false;
      return;
    }

    userSelectChangeRef.current = false;

    if (diff.empty) {
      runApply(proposedMode).catch(() => undefined);
      return;
    }

    setDialogTarget(proposedMode);
    setDialogOpen(true);
  }, [diff, isDiffLoading, isDraft, proposedMode, runApply]);

  useEffect(() => {
    if (isDiffError) {
      setDiffError(t('lecturer.moduleEditor.workingMode.messages.diffError'));
      reportApiError(diffQueryError, t('lecturer.moduleEditor.workingMode.messages.diffError'));
      setProposedMode(persistedMode);
      return;
    }
    setDiffError(null);
  }, [diffQueryError, isDiffError, persistedMode, reportApiError, t]);

  const handleSelectChange = (value: string) => {
    const nextMode = value as WorkingModeWire;
    if (nextMode === proposedMode) return;
    userSelectChangeRef.current = true;
    setProposedMode(nextMode);
  };

  const handleReviewApply = () => {
    setDialogTarget(proposedMode);
    setApplyError(null);
    setDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setApplyError(null);
    setProposedMode(persistedMode);
  };

  const handleDialogConfirm = async () => {
    try {
      await runApply(dialogTarget);
    } catch {
      // Error handled in mutation onError; dialog stays open.
    }
  };

  const blockTokens = useMemo(() => {
    switch (visualState) {
      case 'in_sync':
        return {
          bg: '$statusSuccessSubtle',
          accentColor: '$statusSuccess',
          textColor: '$textOnSuccessSubtle',
          headingColor: '$textHeading',
        };
      case 'version_locked':
        return {
          bg: '$statusInfoSubtle',
          accentColor: '$statusInfo',
          textColor: '$textOnInfoSubtle',
          headingColor: '$textHeading',
        };
      case 'custom':
        return {
          bg: '$bgSubtle',
          accentColor: '$borderStrong',
          textColor: '$textBody',
          headingColor: '$textHeading',
        };
      default:
        return {
          bg: '$bgBrandSubtle',
          accentColor: '$brandBase',
          textColor: '$textOnBrandSubtle',
          headingColor: '$textHeading',
        };
    }
  }, [visualState]);

  const badgeCopy = useMemo(() => {
    switch (visualState) {
      case 'custom':
        return t('lecturer.moduleEditor.workingMode.badges.noPresetRules');
      case 'nothing_to_change':
        return t('lecturer.moduleEditor.workingMode.badges.savedNothingToChange');
      case 'drifted':
        return t('lecturer.moduleEditor.workingMode.badges.changesToApply', { count: changeCount });
      case 'applying':
        return t('lecturer.moduleEditor.workingMode.badges.readOnly');
      case 'in_sync':
        return t('lecturer.moduleEditor.workingMode.badges.inSync');
      case 'version_locked':
        return t('lecturer.moduleEditor.workingMode.badges.versionLockedReadOnly', {
          state: lockedStateLabel,
        });
      default:
        return '';
    }
  }, [changeCount, lockedStateLabel, t, visualState]);

  const descriptionCopy = useMemo(() => {
    switch (visualState) {
      case 'custom':
        return '';
      case 'nothing_to_change':
        return t('lecturer.moduleEditor.workingMode.descriptions.nothingToChange', {
          mode: t(MODE_LABEL_KEYS[persistedMode]),
        });
      case 'drifted':
        return t('lecturer.moduleEditor.workingMode.descriptions.drifted', {
          mode: t(MODE_LABEL_KEYS[persistedMode]),
        });
      case 'applying':
        return t('lecturer.moduleEditor.workingMode.descriptions.applying');
      case 'in_sync':
        return t('lecturer.moduleEditor.workingMode.descriptions.inSync', {
          mode: t(MODE_LABEL_KEYS[persistedMode]),
        });
      case 'version_locked':
        return t('lecturer.moduleEditor.workingMode.descriptions.versionLocked');
      default:
        return '';
    }
  }, [persistedMode, t, visualState]);

  const showSelect = isDraft && visualState !== 'applying';
  const showReviewApply = isDraft && visualState === 'drifted' && !isApplying;
  const reviewDisabled = formIsDirty || isDiffLoading;
  const showActionsColumn = showSelect || showReviewApply || visualState === 'applying';
  const modeInfoRules = persistedMode === 'custom'
    ? []
    : MODE_INFO_RULE_KEYS[persistedMode];
  const showModeInfo = visualState === 'in_sync' && modeInfoRules.length > 0;

  return (
    <>
      <ContentGroup
        testID="working-mode-block"
        variant="accent"
        accentColor={blockTokens.accentColor}
        bg={blockTokens.bg}
        p="$3"
        mb="$4"
      >
        <HStack space="md" alignItems="flex-start" justifyContent="space-between">
          <VStack space="xs" flex={1}>
            <Text size="xs" color="$textHint" textTransform="uppercase" letterSpacing="$md">
              {t('lecturer.moduleEditor.workingMode.label')}
            </Text>
            <HStack space="sm" alignItems="center" flexWrap="wrap">
              <Text size="md" fontWeight="semibold" color={blockTokens.headingColor}>
                {t(MODE_LABEL_KEYS[persistedMode])}
              </Text>
              <Badge size="sm" action="muted" borderRadius="$full">
                <BadgeText>{badgeCopy}</BadgeText>
              </Badge>
            </HStack>
            {(descriptionCopy || showModeInfo) && (
              <HStack space="xs" alignItems="flex-start">
                {!!descriptionCopy && (
                  <Text size="sm" color={blockTokens.textColor} flexShrink={1}>
                    {descriptionCopy}
                  </Text>
                )}
                {showModeInfo && (
                  <Popover
                    isOpen={infoOpen}
                    onClose={() => setInfoOpen(false)}
                    onOpen={() => setInfoOpen(true)}
                    placement="bottom"
                    trigger={(triggerProps) => (
                      <Pressable
                        {...triggerProps}
                        testID="working-mode-info"
                        accessibilityRole="button"
                        accessibilityLabel={t('lecturer.moduleEditor.workingMode.info.a11yLabel', {
                          mode: t(MODE_LABEL_KEYS[persistedMode]),
                        })}
                        onPress={() => setInfoOpen(true)}
                        p="$0.5"
                        mt="$0.5"
                      >
                        <Icon as={Info} size="xs" color={blockTokens.textColor} />
                      </Pressable>
                    )}
                  >
                    <PopoverBackdrop />
                    <PopoverContent w={360} maxWidth="90%">
                      <PopoverHeader borderBottomWidth={1} borderBottomColor="$borderLight">
                        <Heading size="sm" color="$textHeading">
                          {t('lecturer.moduleEditor.workingMode.info.title', {
                            mode: t(MODE_LABEL_KEYS[persistedMode]),
                          })}
                        </Heading>
                        <PopoverCloseButton />
                      </PopoverHeader>
                      <PopoverBody>
                        <VStack space="sm" py="$1">
                          {modeInfoRules.map((ruleKey) => (
                            <HStack key={ruleKey} space="sm" alignItems="flex-start">
                              <Text size="sm" color="$textSubtle">•</Text>
                              <Text size="sm" color="$textBody" flex={1}>
                                {t(ruleKey)}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                )}
              </HStack>
            )}
            {visualState === 'applying' && !reduceMotion && trackedJob?.progress != null && (
              <Box w="$full" mt="$1">
                <Progress value={trackedJob.progress} size="xs">
                  <ProgressFilledTrack />
                </Progress>
              </Box>
            )}
            {visualState === 'drifted' && (
              <VStack space="xs" mt="$2" pt="$2" borderTopWidth={1} borderTopColor="$borderBrand">
                {ledger.policyCount > 0 && (
                  <Text size="xs" color={blockTokens.textColor}>
                    {t('lecturer.moduleEditor.workingMode.ledger.policies', {
                      count: ledger.policyCount,
                    })}
                  </Text>
                )}
                {ledger.essayTaskCount > 0 && (
                  <Text size="xs" color={blockTokens.textColor}>
                    {t('lecturer.moduleEditor.workingMode.ledger.essays', {
                      count: ledger.essayTaskCount,
                    })}
                  </Text>
                )}
                {ledger.structureCount > 0 && (
                  <Text size="xs" color={blockTokens.textColor}>
                    {t('lecturer.moduleEditor.workingMode.ledger.structureMissing')}
                  </Text>
                )}
              </VStack>
            )}
            {diffError && (
              <HStack space="sm" alignItems="center" mt="$1">
                <Text size="xs" color="$statusError">{diffError}</Text>
                <Button
                  size="xs"
                  variant="link"
                  onPress={() => { refetchDiff().catch(() => undefined); }}
                >
                  <ButtonText>{t('lecturer.moduleEditor.workingMode.actions.tryAgain')}</ButtonText>
                </Button>
              </HStack>
            )}
          </VStack>

          {showActionsColumn && (
            <VStack space="sm" alignItems="flex-end" minWidth={180}>
              {showSelect && (
                <Box minWidth={200}>
                  <Select
                    selectedValue={proposedMode}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger
                      testID="working-mode-select"
                      size="sm"
                      variant="outline"
                      bg="$bgCard"
                      minHeight={36}
                    >
                      <SelectInput
                        fontSize={13}
                        color="$textHeading"
                        value={t(MODE_LABEL_KEYS[proposedMode])}
                      />
                      <SelectIcon as={ChevronDownIcon} mr="$2" color="$textSubtle" />
                    </SelectTrigger>
                    <SelectPortal>
                      <SelectBackdrop />
                      <SelectContent>
                        <SelectDragIndicatorWrapper>
                          <SelectDragIndicator />
                        </SelectDragIndicatorWrapper>
                        {WORKING_MODE_OPTIONS.map((mode) => (
                          <SelectItem
                            key={mode}
                            label={t(MODE_LABEL_KEYS[mode])}
                            value={mode}
                          />
                        ))}
                      </SelectContent>
                    </SelectPortal>
                  </Select>
                </Box>
              )}

              {showReviewApply && (
                <VStack alignItems="flex-end" space="xs">
                  <Button
                    testID="working-mode-review-apply"
                    size="sm"
                    minHeight={36}
                    onPress={handleReviewApply}
                    isDisabled={reviewDisabled}
                  >
                    <ButtonText>
                      {t('lecturer.moduleEditor.workingMode.actions.reviewApply', {
                        count: changeCount,
                      })}
                    </ButtonText>
                  </Button>
                  {formIsDirty && (
                    <Text size="2xs" color="$textHint">
                      {t('lecturer.moduleEditor.workingMode.hints.saveFirst')}
                    </Text>
                  )}
                </VStack>
              )}

              {visualState === 'applying' && (
                <HStack space="sm" alignItems="center">
                  {!reduceMotion && <Spinner size="small" color="$brandBase" />}
                  <Text size="sm" color={blockTokens.textColor}>
                    {t('lecturer.moduleEditor.workingMode.states.applying')}
                  </Text>
                </HStack>
              )}
            </VStack>
          )}
        </HStack>
      </ContentGroup>

      <WorkingModeApplyDialog
        isOpen={dialogOpen}
        targetMode={dialogTarget}
        activities={diff?.activities ?? []}
        tasks={tasks}
        isApplying={applyMutation.isPending}
        applyError={applyError}
        onCancel={handleDialogCancel}
        onConfirm={handleDialogConfirm}
      />
    </>
  );
};

export default WorkingModeBlock;
